import { motion } from "framer-motion";
import { FaWhatsapp } from "react-icons/fa";
import { ArrowRight } from "lucide-react";
import { BRAND, PRICING } from "@/lib/config";

const EASE = [0.76, 0, 0.24, 1];

const MaskLine = ({ children, delay = 0 }) => (
  <span className="mask-line">
    <motion.span
      className="inline-block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero({ onBuyNow }) {
  return (
    <section
      id="top"
      className="relative pt-24 sm:pt-28 pb-8 sm:pb-10 overflow-hidden"
      data-testid="hero-section"
    >
      <div className="max-w-[1440px] mx-auto px-6 sm:px-10">
        {/* Eyebrow */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="flex items-center gap-3 mb-5 sm:mb-8"
        >
          <span className="h-px w-8 bg-terracotta" />
          <span className="text-xs sm:text-sm uppercase tracking-[0.24em] font-semibold text-terracotta">
            A 21-day WhatsApp cohort
          </span>
        </motion.div>

        {/* Kinetic headline */}
        <h1
          className="font-serif text-[2.5rem] sm:text-6xl lg:text-7xl xl:text-8xl leading-[1.02] tracking-tight text-ink max-w-[18ch]"
          data-testid="hero-headline"
        >
          <MaskLine delay={0.15}>
            Kindle the{" "}
            <span className="italic font-normal text-terracotta">wonder</span>{" "}
            inside your little one.
          </MaskLine>
        </h1>

        {/* Copy + CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 sm:mt-10 max-w-2xl"
        >
          <p className="font-sans text-lg sm:text-xl leading-relaxed text-ink/75">
            {`For Indian parents of 3–5 year olds. Every morning for 21 days, you'll receive one warm WhatsApp message — a `}
            <span className="text-ink font-semibold">10–15 minute activity</span>
            {` you can do with your child using things already at home. No screens. Just science, stories, questions, and a little bit of magic.`}
          </p>

          <div
            className="mt-8 sm:mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
            data-testid="hero-cta-group"
          >
            <button
              onClick={onBuyNow}
              className="group relative inline-flex items-center gap-3 rounded-full bg-terracotta text-cream px-7 py-4 text-base font-semibold hover:bg-terracotta-hover transition-colors btn-focus"
              data-testid="hero-buy-now-btn"
            >
              <span>
                Enroll for {PRICING.currency}
                {PRICING.launch}
              </span>
              <span className="text-cream/60 line-through text-sm font-normal">
                {PRICING.currency}
                {PRICING.mrp.toLocaleString("en-IN")}
              </span>
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>

            <a
              href={BRAND.whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 rounded-full border border-ink/25 px-6 py-4 text-base font-semibold text-ink hover:bg-ink hover:text-cream transition-colors btn-focus"
              data-testid="hero-whatsapp-btn"
            >
              <FaWhatsapp className="text-whatsapp text-lg" />
              Connect for more details
            </a>
          </div>

          <div className="mt-6 text-xs uppercase tracking-[0.2em] text-ink/50">
            Launch price · Limited seats
          </div>
        </motion.div>
      </div>
    </section>
  );
}
